import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Info } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ConfigureDoorModal from "@/components/ConfigureDoorModal";
import { GARAGE_MODELS } from "@/lib/catalog";

export default function GarageCollectionPage() {
  const [modalModel, setModalModel] = useState(null);

  return (
    <div className="min-h-screen bg-brand-bone flex flex-col">
      <Navbar />
      <main className="flex-1 pt-24 md:pt-28 pb-16 md:pb-24">
        <section className="max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Header */}
          <div className="mb-8 md:mb-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[0.62rem] tracking-[0.28em] uppercase text-brand-ink/60 hover:text-brand-green"
              data-testid="back-to-home"
            >
              <ArrowLeft size={13} /> Back
            </Link>
            <div className="mt-4 text-[0.72rem] tracking-[0.32em] uppercase text-brand-gold">
              Garage Collection
            </div>
            <h1 className="mt-3 font-serif text-4xl md:text-6xl text-brand-green leading-[1.02]">
              Choose your <span className="italic text-brand-gold">design</span>
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-brand-ink/75 leading-relaxed">
              Choose a signature design and enter your opening size.
            </p>
          </div>

          {/* Made-to-order notice — top of page, prominent */}
          <div
            className="mb-10 md:mb-14 border-l-4 border-brand-gold bg-brand-green text-brand-bone px-5 md:px-6 py-4 md:py-5 flex items-start gap-3"
            data-testid="made-to-order-notice"
          >
            <Info size={18} className="text-brand-gold shrink-0 mt-0.5" />
            <p className="text-[0.78rem] md:text-[0.86rem] leading-relaxed">
              <span className="tracking-[0.16em] uppercase text-brand-gold font-medium mr-2">
                Made-to-order
              </span>
              The photos below are catalog examples of each design language. Every garage door is
              made-to-order — colors, finishes and hardware are hand-selected with our atelier team
              once your estimate has been prepared.
            </p>
          </div>

          {/* Model grid */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
            data-testid="garage-model-grid"
          >
            {GARAGE_MODELS.map((m, i) => (
              <motion.button
                key={m.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.5 }}
                onClick={() => setModalModel({ ...m, category: "Garage" })}
                className="group text-left bg-white border border-brand-green/10 hover:border-brand-gold transition-all duration-300 hover:shadow-lg overflow-hidden"
                data-testid={`model-card-garage-${m.id}`}
              >
                <div className="aspect-[16/10] bg-brand-bone-2 overflow-hidden">
                  <img
                    src={m.image}
                    alt={m.name}
                    loading="lazy"
                    className={`w-full h-full ${m.imageFit === "contain" ? "object-contain" : "object-cover group-hover:scale-[1.03]"} transition-transform duration-700`}
                    data-testid={`garage-model-image-${m.id}`}
                  />
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="font-serif text-xl md:text-2xl text-brand-green leading-snug" data-testid={`garage-model-name-${m.id}`}>
                    {m.name}
                  </h3>
                  <p className={`mt-2 text-[0.72rem] text-brand-ink/60 leading-relaxed ${m.showFullDescription ? "" : "line-clamp-2"}`} data-testid={`garage-model-description-${m.id}`}>
                    {m.description}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[0.62rem] tracking-[0.22em] uppercase text-brand-green group-hover:text-brand-gold transition-colors">
                    Configure →
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <ConfigureDoorModal
        open={!!modalModel}
        model={modalModel}
        onClose={() => setModalModel(null)}
      />
    </div>
  );
}
